import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import {
  PlatformNotificationItem,
  PlatformNotificationService,
} from 'src/app/core/services/platform-notification.service';
import {
  NotificationRow,
  NotificationsTableComponent,
} from './notifications-table/notifications-table.component';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    CommonModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    NotificationsTableComponent,
  ],
  templateUrl: './notifications.component.html',
})
export class NotificationsComponent implements OnInit {
  private readonly notificationService = inject(PlatformNotificationService);
  private readonly router = inject(Router);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search notifications…' },
      status: {
        options: [
          { value: '', label: 'All' },
          { value: 'unread', label: 'Unread' },
          { value: 'read', label: 'Read' },
        ],
      },
      dateRange: false,
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  private allItems: PlatformNotificationItem[] = [];
  loading = false;
  listError = '';
  page = 1;
  readonly pageSize = 20;
  meta: ListModulePaginationMeta | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.listError = '';
    this.notificationService
      .listNotifications(1, 100, false)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data) => {
          this.allItems = data?.data ?? [];
          this.refreshMeta();
        },
        error: () => {
          this.allItems = [];
          this.meta = null;
          this.listError = 'Could not load notifications.';
        },
      });
  }

  private get filteredItems(): PlatformNotificationItem[] {
    const status = this.listFilters.status;
    const q = this.listFilters.searchQuery.trim().toLowerCase();
    return this.allItems.filter((n) => {
      if (status === 'unread' && n.is_read) return false;
      if (status === 'read' && !n.is_read) return false;
      if (!q) return true;
      return (
        (n.title ?? '').toLowerCase().includes(q) ||
        (n.message ?? '').toLowerCase().includes(q) ||
        (n.type ?? '').toLowerCase().includes(q)
      );
    });
  }

  get rows(): NotificationRow[] {
    const filtered = this.filteredItems;
    const offset = (this.page - 1) * this.pageSize;
    return filtered
      .slice(offset, offset + this.pageSize)
      .map((n, i) => ({ ...n, srNo: offset + i + 1 }));
  }

  get hasUnread(): boolean {
    return this.allItems.some((n) => !n.is_read);
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.refreshMeta();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
  }

  private refreshMeta(): void {
    const total = this.filteredItems.length;
    const lastPage = Math.max(1, Math.ceil(total / this.pageSize));
    if (this.page > lastPage) this.page = lastPage;
    this.meta = { total, lastPage, currentPage: this.page, perPage: this.pageSize };
  }

  onMarkRead(payload: { row: PlatformNotificationItem; event: Event }): void {
    payload.event.stopPropagation();
    const item = payload.row;
    if (item.is_read) return;
    item.is_read = true;
    this.notificationService.markRead(item.id).subscribe();
    this.refreshMeta();
  }

  markAllRead(): void {
    this.allItems = this.allItems.map((n) => ({ ...n, is_read: true }));
    this.notificationService.markAllRead().subscribe();
    this.refreshMeta();
  }

  openNotification(item: PlatformNotificationItem): void {
    if (!item.is_read) {
      item.is_read = true;
      this.notificationService.markRead(item.id).subscribe();
      this.refreshMeta();
    }
    const url = item.meta?.['redirect_url'];
    if (typeof url === 'string' && url) {
      void this.router.navigateByUrl(url);
    }
  }
}
